import React, { useEffect, useMemo, useRef, useState } from 'react';
import connect from 'Shared/connect';
import { styled } from '@glitz/react';
import * as style from 'Shared/Style';
import { debounce } from 'lodash';
import { formatPrice } from 'Shared/number-format';

type PropType = {
  min: number;
  max: number;
  selectedMin: number;
  selectedMax: number;
  culture: string;
  currency: string;
  updateValue: (selectedMin: number, selectedMax: number) => void;
};

const Label = styled.span({
  display: 'inline-block',
  textAlign: 'center', 
  padding: {
    y: '5px',
    x: '8px',
  }, 
  fontSize: '14px',
  textTransform: 'uppercase',
  fontWeight: 500,
  lineHeight: '100%',
  color: style.colors.monochrome.black,
  boxSizing: 'border-box',
})

const LabelWrap = styled.div({
  position: 'absolute',
  zIndex: 2,
  pointerEvents: 'none',
  top: '-5px',
})

const Wrap = styled.div({
  position: 'relative', 
  minHeight: '25px', 
  marginTop: '12px', 
  width: '100%'
})

const FromInput = styled.input({
  position: 'absolute',
  appearance: 'none',
  height: 0,
  width: '100%',
  backgroundColor: style.colors.monochrome.darkGrey,
  zIndex: 1,
  pointerEvents: 'none',
  '::-webkit-slider-thumb': {
    appearance: 'none',
    pointerEvents: 'all',
    height: '24px',
    backgroundColor: style.colors.monochrome.white,
    border: { xy: { width: '1px', style: 'solid', color: style.colors.monochrome.black } },
    borderRadius: '48px',
    cursor: 'pointer',
    padding: {
      y: '5px',
      x: '8px',
    },
  },
})

const ToInput = styled.input({
  position: 'absolute',
  appearance: 'none',
  height: '2px',
  width: '100%',
  backgroundColor: style.colors.monochrome.darkGrey,
  pointerEvents: 'none',
  '::-webkit-slider-thumb': {
    appearance: 'none',
    pointerEvents: 'all',
    height: '24px',
    padding: {
      y: '5px',
      x: '8px',
    },
    backgroundColor: style.colors.monochrome.white,
    border: { xy: { width: '1px', style: 'solid', color: style.colors.monochrome.black } },
    borderRadius: '48px',
    cursor: 'pointer',
  },
})

const DoubleSlider = connect(state => ({
  culture: state.appShellData.culture,
  currency: state.appShellData.market.currency,
}))
(({ min, max, selectedMin, selectedMax, currency, culture, updateValue }: PropType) => {
  
  if(min === max) {
    return <></>;
  }

  const sliderRange = (max - min === 0 ? 1 : max -min);
  const valueToPercentage = (value: number) => {
    return Math.round(((value - min) * 100) / sliderRange);
  };

  const percentageToValue = (percentage: number) => {
    return Math.round((percentage * sliderRange) / 100 + min);
  };
  
  const fromSliderRef = useRef<HTMLInputElement>(null);
  const toSliderRef = useRef<HTMLInputElement>(null);
  const [fromPosition, setFromPosition] = useState(valueToPercentage(selectedMin ?? min));
  const [toPosition, setToPosition] = useState(valueToPercentage(selectedMax ?? max));
  const [sliderFromWidth, setSliderFromWidth] = useState(0);
  const [sliderToWidth, setSliderToWidth] = useState(0);

  const updateValueDebounced = useMemo(() => debounce(updateValue, 1000), [updateValue]);

  useEffect(() => {
    const newSelectedMin = percentageToValue(fromPosition);
    const newSelectedMax = percentageToValue(toPosition);
    if (newSelectedMin !== selectedMin || newSelectedMax !== selectedMax) {
      updateValueDebounced(newSelectedMin, newSelectedMax);
    }
  }, [fromPosition, toPosition]);

  const updateWidth = (e: HTMLSpanElement, isFirst: boolean) => {
    if (e) {
      if(isFirst) {
        setSliderFromWidth(e.offsetWidth);
      } else {
        setSliderToWidth(e.offsetWidth);
      }
    }
  }

  return (
    <Wrap>
      <FromInput
        ref={fromSliderRef}
        type="range"
        value={fromPosition}
        onChange={(e) => setFromPosition(parseInt(e.target.value, 10))}
        css={{
          '::-webkit-slider-thumb': {
            width: `${sliderFromWidth}px`,
          },
        }}
      />
      <LabelWrap
        css={{
          width: `calc(100% - ${sliderFromWidth}px)`,
          transform: `translate(${fromPosition}%, -7px)`,
        }}
      >
        <Label ref={ref => ref ? updateWidth(ref, true) : null}>
          {formatPrice(percentageToValue(fromPosition), currency, culture)}
        </Label>
      </LabelWrap>
      <ToInput
        ref={toSliderRef}
        type="range"
        value={toPosition}
        onChange={(e) => setToPosition(parseInt(e.target.value, 10))}
        css={{
          backgroundImage: `linear-gradient(
            to right,
            ${style.colors.monochrome.darkGrey} 0%,
            ${style.colors.monochrome.darkGrey} ${fromPosition}%,
            ${style.colors.monochrome.black} ${fromPosition}%,
            ${style.colors.monochrome.black} ${toPosition}%, 
            ${style.colors.monochrome.darkGrey} ${toPosition}%, 
            ${style.colors.monochrome.darkGrey} 100%)`,
          '::-webkit-slider-thumb': {
            width: `${sliderToWidth}px`,
          },
        }}
      />
      <LabelWrap
        css={{
          width: `calc(100% - ${sliderToWidth}px)`,
          transform: `translate(${toPosition}%, -7px)`,
        }}
      >
        <Label ref={ref => ref ? updateWidth(ref, false) : null}>
          {formatPrice(percentageToValue(toPosition), currency, culture)}
        </Label>
      </LabelWrap>
    </Wrap>
  );
});

export default DoubleSlider;
